<template>
  <ul
    role="menubar"
    aria-disabled="false"
    aria-label="Pagination"
    :class="mergedClasses"
    v-bind="passthroughAttrs"
  >
    <li
      role="presentation"
      aria-hidden="true"
      class="page-item"
      :class="{ disabled: currentPage <= 1 }"
    >
      <span
        v-if="currentPage <= 1"
        role="menuitem"
        aria-label="Go to first page"
        class="page-link"
      >
        «
      </span>
      <a
        v-else
        role="menuitem"
        aria-label="Go to first page"
        class="page-link"
        href="#"
        @click.prevent="goToPage(1)"
      >
        «
      </a>
    </li>
    <li
      role="presentation"
      aria-hidden="true"
      class="page-item"
      :class="{ disabled: currentPage <= 1 }"
    >
      <span
        v-if="currentPage <= 1"
        role="menuitem"
        aria-label="Go to previous page"
        class="page-link"
      >
        ‹
      </span>
      <a
        v-else
        role="menuitem"
        aria-label="Go to previous page"
        class="page-link"
        href="#"
        @click.prevent="goToPage(currentPage - 1)"
      >
        ‹
      </a>
    </li>
    <template v-for="item in pageItems">
      <li
        v-if="item.ellipsis"
        :key="item.key"
        role="separator"
        class="page-item disabled"
      >
        <span class="page-link">…</span>
      </li>
      <li
        v-else
        :key="item.key"
        role="presentation"
        class="page-item"
        :class="{ active: item.page === currentPage }"
      >
        <span
          v-if="item.page === currentPage"
          role="menuitemradio"
          aria-label="Go to page"
          class="page-link"
          aria-checked="true"
          aria-posinset="1"
          aria-setsize="1"
        >
          {{ item.page }}
        </span>
        <a
          v-else
          role="menuitemradio"
          aria-label="Go to page"
          class="page-link"
          href="#"
          @click.prevent="goToPage(item.page)"
        >
          {{ item.page }}
        </a>
      </li>
    </template>
    <li
      role="presentation"
      aria-hidden="true"
      class="page-item"
      :class="{ disabled: currentPage >= resolvedPageCount }"
    >
      <span
        v-if="currentPage >= resolvedPageCount"
        role="menuitem"
        aria-label="Go to next page"
        class="page-link"
      >
        ›
      </span>
      <a
        v-else
        role="menuitem"
        aria-label="Go to next page"
        class="page-link"
        href="#"
        @click.prevent="goToPage(currentPage + 1)"
      >
        ›
      </a>
    </li>
  </ul>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HdPagination',

  inheritAttrs: false,

  props: {
    value: {
      type: Number,
      default: 1
    },
    modelValue: {
      type: Number,
      default: undefined
    },
    // Page count can be given directly, or derived from totalRows / perPage.
    // pageCountProp wins when provided (> 0); otherwise ceil(totalRows/perPage).
    totalRows: {
      type: Number,
      default: 0
    },
    perPage: {
      type: Number,
      default: 0
    },
    pageCount: {
      type: Number,
      default: 0
    },
    // Maximum number of numbered page buttons to render. 0 = show every page
    // (the original behaviour). When the page count exceeds the limit, the
    // first and last pages are always shown and the middle is windowed around
    // the current page with … separators.
    limit: {
      type: Number,
      default: 0
    },
    align: {
      type: String,
      default: null,
      validator: (value) => value == null || ['left', 'center', 'right', 'fill'].includes(value)
    }
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    currentPage() {
      return this.modelValue != null ? this.modelValue : this.value;
    },

    resolvedPageCount() {
      if (this.pageCount > 0) return this.pageCount;
      if (this.perPage <= 0) return 1;

      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },

    // Ordered list of items to render between the prev/next chevrons. Each item
    // is either { page } or { ellipsis: true }.
    pageItems() {
      const count = this.resolvedPageCount;
      const current = this.currentPage;
      const limit = this.limit;

      const asPages = (from, to) => {
        const items = [];

        for (let page = from; page <= to; page++) {
          items.push({ page, key: `page-${page}` });
        }

        return items;
      };

      if (limit <= 0 || count <= limit) {
        return asPages(1, count);
      }

      // Centre a window of (limit - 2) pages around the current page, keeping
      // the first and last pages reserved, then clamp it inside [2, count - 1].
      const inner = Math.max(1, limit - 2);
      let start = current - Math.floor((inner - 1) / 2);
      let end = current + Math.ceil((inner - 1) / 2);

      if (start < 2) {
        end += 2 - start;
        start = 2;
      }

      if (end > count - 1) {
        start -= end - (count - 1);
        end = count - 1;
      }

      start = Math.max(2, start);
      end = Math.min(count - 1, end);

      // If exactly one page would be hidden behind a separator, show it instead.
      if (start === 3) start = 2;
      if (end === count - 2) end = count - 1;

      const items = [{ page: 1, key: 'page-1' }];

      if (start > 2) items.push({ ellipsis: true, key: 'ellipsis-start' });
      items.push(...asPages(start, end));
      if (end < count - 1) items.push({ ellipsis: true, key: 'ellipsis-end' });
      items.push({ page: count, key: `page-${count}` });

      return items;
    },

    alignClass() {
      switch (this.align) {
        case 'center':
          return 'justify-content-center';
        case 'right':
          return 'justify-content-end';
        case 'fill':
          return 'justify-content-between';
        default:
          return null;
      }
    },

    mergedClasses() {
      return [
        this.$attrs.class,
        'pagination',
        'b-pagination',
        this.alignClass
      ].flat().filter(Boolean);
    },

    passthroughAttrs() {
      const { class: _class, ...attrs } = this.$attrs;

      return attrs;
    }
  },

  methods: {
    goToPage(page) {
      if (page < 1 || page > this.resolvedPageCount || page === this.currentPage) return;

      this.$emit('input', page);
      this.$emit('update:modelValue', page);
    }
  }
});
</script>
